import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Drawer } from '@mui/material';
import Logo from '@shared/assets/Logo';
import { icons } from '@shared/static/icons';
import { roles, getUserRole } from '@shared/static/roles';
import { Avatar } from '@shared/ui/parts';
import { formatBalanceUi } from '@utils/balance-helpers';
import { userTopMenu, userBottomMenu } from '@utils/userMenu';
import {
  SidebarActions,
  SidebarLink,
  SidebarEnableTwoFa,
  DevStateSwitcher,
} from '@widgets/sidebar/parts';

export const drawerWidth = 204;

export default function Sidebar() {
  const location = useLocation();
  const { user, isFrozen } = useSelector((state) => state.user);
  const { t } = useTranslation();
  const { totalBalance } = useSelector((state) => state.bankWallets);
  const isMember = user.group_role === roles.TEAM_MEMBER;
  const team = user.groups?.[0];

  return (
    <Box
      component="nav"
      position="relative"
      zIndex={0}
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      className="sidebar-container"
    >
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
        open
      >
        <div className="sidebar">
          <div>
            <Link to="/" className="sidebar__logo">
              <Logo />
            </Link>
            <div className="sidebar__menu">
              {/* display team instead of balances for team member */}
              {isMember ? (
                <div className="sidebar__menu-item sidebar__menu-team sidebar__menu-block group-name">
                  <Avatar team={team} />
                  <div>
                    <p className="primary-text">{team?.name}</p>
                    <p className="body-12r">{t('team')}</p>
                  </div>
                </div>
              ) : (
                <div className="sidebar__menu-item sidebar__menu-block balance-block">
                  {icons.balance}
                  <div>
                    <p
                      className="body-18m"
                      dangerouslySetInnerHTML={{
                        __html: formatBalanceUi(
                          totalBalance,
                          'USD',
                          null,
                          true,
                        ),
                      }}
                    />
                    <p className="body-12r">{t('total_balance')}</p>
                  </div>
                </div>
              )}
              {userTopMenu
                .filter((item) => !item.exclude?.includes(user.group_role))
                .map((item) => (
                  <SidebarLink key={item.text} item={item} />
                ))}
            </div>
          </div>
          <div>
            <div className="sidebar__footer">
              <DevStateSwitcher />
              <SidebarEnableTwoFa />
              {userBottomMenu
                .filter((item) => !item.exclude?.includes(user.group_role))
                .map((item) => {
                  if (item.path === '/profile') {
                    return (
                      <Link
                        key={item.text}
                        to="/profile"
                        className={`sidebar__menu-item sidebar__menu-block ${
                          location.pathname === '/profile' ? 'active' : ''
                        } ${isFrozen ? 'disabled' : ''}`}
                      >
                        {isFrozen ? icons.lock : icons.profile}
                        <div>
                          <p className="primary-text fw-400">{user.name}</p>
                          <p className="body-12r">
                            {t(getUserRole(user.group_role))}
                          </p>
                        </div>
                      </Link>
                    );
                  }
                  return <SidebarLink key={item.text} item={item} />;
                })}
            </div>
            <SidebarActions />
          </div>
        </div>
      </Drawer>
    </Box>
  );
}
